import React, { Fragment, useState, useEffect, useContext } from "react";
import {
  Modal,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
} from "reactstrap";
import DataTable, { createTheme } from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { ThemeContext } from "../contexts/ThemeContext";
import { getUserData, addUser, updateUser } from "../redux/actions/userListActions";
import { useDebounce } from "use-debounce"; // npm install use-debounce

const UserList = () => {
  const { theme } = useContext(ThemeContext);
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); // <-- Add search term state
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500); // <-- Debounce search
  const [formData, setFormData] = useState({
    _id: null,
    name: "",
    email: "",
    mobilenumber: "",
    whatsapp: "",
    status: "active",
    password: "",
  });

  const { userListData, loading, pagination } = useSelector(
    (state) => state.userList
  );

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    setFormData({
      _id: null,
      name: "",
      email: "",
      mobilenumber: "",
      whatsapp: "",
      status: "active",
      password: "",
    });
    setIsEdit(false);
  };

  const handleEdit = (user) => {
    setFormData({
      _id: user._id,
      name: user.name,
      email: user.email,
      mobilenumber: user.mobilenumber,
      whatsapp: user.whatsappnumber,
      status: user.status || "active",
      password: "",
    });
    setIsEdit(true);
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (isEdit) {
      await dispatch(updateUser(formData, currentPage, itemsPerPage, debouncedSearchTerm));
    } else {
      const { password, ...restData } = formData;
      await dispatch(
        addUser({ ...restData, password, role: "user" }, currentPage, itemsPerPage, debouncedSearchTerm)
      );
    }
    await dispatch(getUserData(currentPage, itemsPerPage, debouncedSearchTerm));
    toggleModal();
  };

  const columns = [
    { name: <b>S.No</b>, cell: (row, index) => index + 1 + (currentPage - 1) * itemsPerPage },
    { name: <b>Name</b>, selector: (row) => row?.name },
    { name: <b>Email</b>, selector: (row) => row?.email },
    { name: <b>Mobile</b>, selector: (row) => row?.mobilenumber || "N/A" },
    { name: <b>WhatsApp</b>, selector: (row) => row?.whatsappnumber || "N/A" },
    { name: <b>Status</b>, selector: (row) => row?.status || "active" },
    {
      name: <b>Actions</b>,
      cell: (row) => (
        <span onClick={() => handleEdit(row)}>
          <i className="fa fa-pencil p-2" style={{ color: "rgb(40, 167, 69)", cursor: "pointer", fontSize: 20 }}></i>
        </span>
      ),
    },
  ];

  useEffect(() => {
    dispatch(getUserData(currentPage, itemsPerPage, debouncedSearchTerm));
  }, [currentPage, itemsPerPage, debouncedSearchTerm]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setItemsPerPage(newPerPage);
    setCurrentPage(page);
  };

  createTheme("darkMode", {
    text: { primary: "#FFFFFF" },
    background: { default: "#1E1E1E" },
  });

  createTheme("lightMode", {
    text: { primary: "#000000" },
    background: { default: "#FFFFFF" },
  });

  return (
    <Fragment>
      <div className="flex flex-row justify-between items-center mb-3">
        <h4 className="mt-4">User List</h4>
        <Button color="primary" onClick={toggleModal}>
          + Add User
        </Button>
      </div>

      <Container fluid="true">
        <Row>
          <Col sm="12">
            <div className="d-flex justify-content-end mb-2">
              <Input
                type="text"
                placeholder="Search..."
                onChange={e => setSearchTerm(e.target.value)}
                style={{ maxWidth: 300 }}
              />
            </div>
            <Card>
              <CardBody>
                <DataTable
                  pagination
                  columns={columns}
                  data={userListData || []}
                  theme={theme === "dark" ? "darkMode" : "lightMode"}
                  paginationServer
                  paginationTotalRows={pagination?.totalItems || 0}
                  paginationPerPage={itemsPerPage}
                  onChangePage={handlePageChange}
                  onChangeRowsPerPage={handlePerRowsChange}
                  progressPending={loading}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          {isEdit ? "Edit User" : "Add New User"}
        </ModalHeader>
        <ModalBody>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              className="form-control"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
          </div>
          <div className="form-group mt-2">
            <label>Email</label>
            <input
              type="email"
              className="form-control"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
          </div>
          <div className="form-group mt-2">
            <label>Mobile Number</label>
            <input
              type="text"
              className="form-control"
              value={formData.mobilenumber}
              onChange={(e) => setFormData({ ...formData, mobilenumber: e.target.value })}
            />
          </div>
          <div className="form-group mt-2">
            <label>WhatsApp Number</label>
            <input
              type="text"
              className="form-control"
              value={formData.whatsapp}
              onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
            />
          </div>
          <div className="form-group mt-2">
            <label>Status</label>
            <select
              className="form-control"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="active">Active</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>

          {!isEdit && (
            <div className="form-group mt-2">
              <label>Password</label>
              <input
                type="text"
                className="form-control"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSubmit}>
            {isEdit ? "Update" : "Submit"}
          </Button>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default UserList;
